import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ApolloQueryResult } from '@apollo/client/core';
import { Apollo, MutationResult } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  BusinessUnit,
  BusinessUnitIn,
  GetAllBusinessEntitiesIdDocument,
  GetAllBusinessEntitiesIdQuery,
  GetBusinessUnitDocument,
  GetBusinessUnitQuery,
  GetBusinessUnitSummaryDocument,
  SaveBusinessUnitDocument,
  SaveBusinessUnitMutation,
  SaveBusinessUnitMutationVariables,
  UpdateBusinessUnitActiveDocument,
  UpdateBusinessUnitActiveMutation,
  UpdateBusinessUnitActiveMutationVariables,
  UpdateBusinessUnitDocument,
  UpdateBusinessUnitMutation,
  UpdateBusinessUnitMutationVariables,
} from 'src/app/graphql/types';
import { environment } from 'src/environments/environment.test';
import { BusinessUnitObjectsJson } from './microservices/json/business-unit-elements.json';

@Injectable({
  providedIn: 'root',
})
export class BusinessUnitService {
  private endpoint = environment.IDEMPIER_GATEWAY + '/v1/business-unit';
  private http = inject(HttpClient);
  private readonly apollo = inject(Apollo);

  getByBranchOfficeId(
    branchOffideId: number,
  ): Observable<ApolloQueryResult<GetBusinessUnitQuery>> {
    return this.apollo.watchQuery<GetBusinessUnitQuery>({
      query: GetBusinessUnitDocument,
      variables: { id: branchOffideId },
    }).valueChanges;
  }

  getBusinessUnitSummary(
    branchOfficeId: number,
  ): Observable<BusinessUnit[] | null> {
    return this.apollo
      .watchQuery<{ getByBranchOfficeId: BusinessUnit[] }>({
        query: GetBusinessUnitSummaryDocument,
        variables: {
          id: branchOfficeId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getByBranchOfficeId : null;
        }),
      );
  }

  save(form: FormGroup): Observable<MutationResult<SaveBusinessUnitMutation>> {
    return this.apollo.mutate<
      SaveBusinessUnitMutation,
      SaveBusinessUnitMutationVariables
    >({
      mutation: SaveBusinessUnitDocument,
      variables: {
        json: this.businessUnitIn(form),
      },
    });
  }

  update(
    form: FormGroup,
  ): Observable<MutationResult<UpdateBusinessUnitMutation>> {
    return this.apollo.mutate<
      UpdateBusinessUnitMutation,
      UpdateBusinessUnitMutationVariables
    >({
      mutation: UpdateBusinessUnitDocument,
      variables: {
        json: this.businessUnitIn(form),
      },
    });
  }

  getAllBusinessEntitiesId(
    businessUnitId: number,
  ): Observable<BusinessUnitObjectsJson | null> {
    return this.apollo
      .watchQuery<GetAllBusinessEntitiesIdQuery>({
        query: GetAllBusinessEntitiesIdDocument,
        variables: { businessUnitId },
        errorPolicy: 'all',
      })
      .valueChanges.pipe(
        map(result => {
          if (result.data) {
            return {
              businessDirection: result.data.getBusinessDirectionById ?? null,
              economicNature: result.data.getEconomicNatureById ?? null,
              cashRegister: result.data.getCashRegisterByBusinessUnitId ?? null,
              warehouse: result.data.getWarehouseByBusinessUnitId ?? null,
            } as BusinessUnitObjectsJson;
          } else return null;
        }),
      );
  }

  updateBusinessUnitActive(
    id: number,
    active: boolean,
  ): Observable<MutationResult<UpdateBusinessUnitActiveMutation>> {
    return this.apollo.mutate<
      UpdateBusinessUnitActiveMutation,
      UpdateBusinessUnitActiveMutationVariables
    >({
      mutation: UpdateBusinessUnitActiveDocument,
      variables: {
        id: id.toString(),
        active: active,
      },
    });
  }

  private businessUnitIn(form: FormGroup): BusinessUnitIn {
    const idBusiness = form.get('id')?.value || '0';

    return {
      id: idBusiness,
      branchOfficeId: form.get('branchOfficeId')?.value,
      active: true,
      businessDirection: {
        businessUnitId: idBusiness,
        canton: {
          id: form.get('canton')?.value,
          provinceId: form.get('province')?.value,
        },
        street: form.get('street')?.value,
        streetNumber: Number(form.get('streetNumber')?.value),
        intersection: form.get('intersection')?.value,
        storeName: form.get('storeName')?.value,
      },
    };
  }
}
